import { useEffect, useState } from "react"
import { Helmet } from "react-helmet-async"
import { Link } from "react-router-dom"
import { CheckCircle2, Clock, ChevronRight, Star, Award, MapPin } from "lucide-react"

export default function Packages() {
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)
  const [locations, setLocations] = useState([])
  const [selectedLocation, setSelectedLocation] = useState("")

  useEffect(() => {
    fetch("/apis/web/clinics")
      .then(res => res.json())
      .then(data => {
        if (data.result) {
          const locs = data.result.map(c => ({ id: c.Hospital_Id, name: c.HospitalName }))
          setLocations(locs)
          if (locs.length > 0) setSelectedLocation(locs[0].id)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!selectedLocation) return
    setLoading(true)
    fetch(`/apis/web/healthpackage?hospitalId=${selectedLocation}`)
      .then(res => res.json())
      .then(res => {
        if (res?.success && Array.isArray(res.data)) {
          setPackages(res.data)
        } else {
          setPackages([])
        }
      })
      .catch(err => { console.error("Error fetching packages:", err); setPackages([]) })
      .finally(() => setLoading(false))
  }, [selectedLocation])

  const VISIBLE_FEATURES = 3

  return (
    <>
      <Helmet>
        <title>Health Packages | Pranaam Hospitals</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary/10 to-primary/5 py-16 md:py-24">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Health <span className="text-primary">Packages</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700 max-w-3xl mx-auto">
                Pranaam Health Packages offer comprehensive preventive check-ups with advanced diagnostics and expert care to keep you healthy and ahead of potential health risks.
              </p>
            </div>
          </div>
        </section>

        {/* Women Care Packages
        <section className="py-14 md:py-20 text-center">
          <div className="container px-4 mx-auto">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              <span className="text-primary">Women Care</span> Packages
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
              Comprehensive healthcare packages designed specifically for women's unique needs at every stage of life
            </p>
          </div>
        </section> */}

        {/* Packages */}
        <section className="py-10 md:py-16">
          <div className="container px-4 mx-auto">
            <div className="flex items-center justify-end mb-6 max-w-6xl mx-auto">
              <div className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-white shadow-sm">
                <MapPin className="w-4 h-4 text-primary" />
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="border-none outline-none bg-transparent text-sm font-medium cursor-pointer"
                >
                  <option value="" disabled>Select Location</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {packages.map((pkg, index) => {
                  const features = Array.isArray(pkg.services) ? pkg.services.map(s => s.servName) : []
                  const badge = index === 0 ? "Popular" : index === 1 ? "Recommended" : ""
                  const badgeBg = badge === "Popular" ? "bg-pink-600" : "bg-teal-600"
                  const BadgeIcon = badge === "Popular" ? Star : Award

                  return (
                    <div key={pkg.servCode} className="bg-white rounded-2xl border border-gray-200 flex flex-col h-full hover:shadow-lg transition-shadow duration-300">
                      {/* Content */}
                      <div className="px-5 pt-5 pb-5 flex flex-col flex-1">
                        {badge && (
                          <div className="mb-3">
                            <span className={`${badgeBg} text-white text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1.5`}>
                              <BadgeIcon className="w-3.5 h-3.5" />
                              {badge}
                            </span>
                          </div>
                        )}

                        {/* Title & Price */}
                        <h3 className="text-lg font-bold text-gray-900 mb-1">{pkg.servName}</h3>
                        <p className="text-sm text-gray-500 mb-1">{pkg.hospitalName}</p>
                        <p className="text-2xl font-bold text-primary mb-4">₹{pkg.serviceCost}/-</p>

                        {/* Features */}
                        <div className="space-y-2.5 mb-2">
                          {features.slice(0, VISIBLE_FEATURES).map((feature, i) => (
                            <div key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                              <CheckCircle2 className="w-4.5 h-4.5 text-green-500 flex-shrink-0" />
                              {feature.trim()}
                            </div>
                          ))}
                        </div>
                        {features.length > VISIBLE_FEATURES && (
                          <p className="text-xs text-gray-400 mb-4">+{features.length - VISIBLE_FEATURES} more inclusions</p>
                        )}

                        {/* Button */}
                        <div className="mt-auto pt-3">
                          <Link
                            to={`/packages/${pkg.servCode}?location=${selectedLocation}`}
                            className="w-full flex items-center justify-center gap-2 bg-primary text-white text-sm font-semibold py-3.5 rounded-xl hover:bg-primary/90 transition-colors"
                          >
                            View Package Details
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  )
}
